import { useState } from "react";
import { parseUnits, formatUnits } from "ethers";
import { Plus } from "lucide-react";
import { findPair, pairReserves, dexAddLiquidity } from "@/lib/dex";
import { AmountBox, ActionBtn, ImportToken } from "@/components/dex/DexParts";

const toRaw = (v, d) => { try { return v && Number(v) > 0 ? parseUnits(String(v), d) : 0n; } catch { return 0n; } };

export const DexAddPanel = ({ dex, address, run, busy, step }) => {
  const { tokens, pairs, router, importToken } = dex;
  const [tokenA, setTokenA] = useState(null);
  const [tokenB, setTokenB] = useState(null);
  const [amountA, setAmountA] = useState("");
  const [amountBInput, setAmountB] = useState("");
  const pair = tokenA && tokenB ? findPair(pairs, tokenA, tokenB) : null;
  const hasLiquidity = pair && BigInt(pair.reserve0Raw) > 0n;
  const rawA = tokenA ? toRaw(amountA, tokenA.decimals) : 0n;
  let rawB = tokenB ? toRaw(amountBInput, tokenB.decimals) : 0n;
  if (hasLiquidity && rawA) {
    const [ra, rb] = pairReserves(pair, tokenA);
    rawB = (rawA * rb) / ra;
  }
  const amountB = hasLiquidity ? (rawA ? formatUnits(rawB, tokenB.decimals) : "") : amountBInput;
  const same = tokenA && tokenB && tokenA.address === tokenB.address;

  const onAdd = () => run((onStep) => dexAddLiquidity({ eip1193: address.eip1193, routerAddr: router, tokenA, tokenB, amountA: rawA, amountB: rawB, onStep })
    .then((h) => { setAmountA(""); setAmountB(""); return h; }), pair ? `Added liquidity to ${tokenA.symbol}/${tokenB.symbol}` : `Created the ${tokenA.symbol}/${tokenB.symbol} pair`);

  return (
    <div data-testid="dex-add-panel" className="space-y-3">
      <AmountBox label="Token A" amount={amountA} onAmount={setAmountA} token={tokenA} tokens={tokens} onToken={setTokenA} testId="dex-add-a" />
      <div className="flex justify-center"><Plus size={16} className="text-[#D4AF37]" /></div>
      <AmountBox label={hasLiquidity ? "Token B (matches the pair's ratio)" : "Token B"} amount={amountB} onAmount={setAmountB}
        token={tokenB} tokens={tokens} onToken={setTokenB} testId="dex-add-b" readOnly={hasLiquidity} />
      {tokenA && tokenB && !same && (
        <p data-testid="dex-add-status" className="text-xs text-zinc-500">
          {hasLiquidity ? `Adding to the existing ${tokenA.symbol}/${tokenB.symbol} pair.` : `No ${tokenA.symbol}/${tokenB.symbol} pair yet: you create it and your amounts set its starting price.`}
        </p>
      )}
      {same && <p className="text-xs text-red-400">Pick two different tokens.</p>}
      <ImportToken onImport={importToken} />
      <ActionBtn testId="dex-add-submit" busy={busy} step={step} disabled={!address.account || !rawA || !rawB || same} onClick={onAdd}>
        {address.account ? (pair ? "Add liquidity" : "Create pair & add liquidity") : "Connect wallet to add"}
      </ActionBtn>
    </div>
  );
};
